// Каталог слов каркаса: шапка, индикатор свежести, состояния оболочки.
//
// ⚠⚠ **Здесь не слова, а коды** (`G117`, таск 74): слово к коду живёт в словаре
// `data/screen-words.json`, и каталог читает его в момент показа
// (`@/api/words`, `fromDictionary`). Клиент своих слов не держит; одно понятие —
// одно слово на всех экранах.
//
// ⚠ Строки каркаса отделены от строк поверхностей: строки поверхности живут в
// её папке (`src/surfaces/S-NN/uk.ts`), иначе общий каталог правил бы каждый
// таск сразу (`interfaces.md` §2.6). Слова общих компонентов состояний —
// `src/states/uk.ts` (названное исключение, причина там).

import { fromDictionary, hint } from "@/api/words";

/** Имя системы — латиницей, как торговая марка; в словарь не идёт. */
const appName = "DSM Fortify";

/** Каркас: то, что видно до входа, — нужно до входа. */
const shell = fromDictionary(
  {
    loading: "shell_loading",
    noSurfaces: "shell_no_surfaces",
    noSurfacesHint: "shell_no_surfaces_hint",
    notFound: "shell_not_found",
    notFoundHint: "shell_not_found_hint",
    failed: "shell_failed",
    /** Ссылка «до змісту» — первая в порядке фокуса, есть и на входе. */
    skip: "shell_skip",
  },
  { beforeLogin: true },
);

/**
 * Меню и профиль в шапке — только после входа: в них названия поверхностей и
 * логин (раздел `S-00`: до входа ни названий поверхностей, ни учётных записей).
 * Подписи пунктов и групп меню приходят из `CFG-141` (решение оркестратора 58),
 * здесь — только слова самой оболочки.
 */
const nav = fromDictionary({
  menu: "shell_menu",
  open: "shell_menu_open",
  close: "shell_menu_close",
  home: "shell_home",
  /** Для чтеца у свёрнутого раздела, внутри которого текущий экран. */
  here: "shell_menu_here",
  /** Меню не пришло — строка вместо пустого места. */
  silent: "shell_menu_silent",
  /** Поиск по экранам меню: кнопка, окно, поле, пустая выдача. */
  search: "shell_menu_search",
  searchTitle: "shell_menu_search_title",
  searchPlaceholder: "shell_menu_search_placeholder",
  searchEmpty: "shell_menu_search_empty",
  /** Итог поиска вслух: «3 екрани». */
  searchFound: { one: "shell_menu_search_found_one", few: "shell_menu_search_found_few", many: "shell_menu_search_found_many" },
  /** Нижняя строка окна поиска: «↑↓ вибір · Enter відкрити · Esc закрити». */
  searchHintPick: "shell_menu_search_hint_pick",
  searchHintOpen: "shell_menu_search_hint_open",
  searchHintClose: "shell_menu_search_hint_close",
  /** Звёздочка у пункта, сбой записи; отмеченные стоят сверху без заголовка (решение 067). */
  favAdd: "shell_menu_fav_add",
  favRemove: "shell_menu_fav_remove",
  favFailed: "shell_menu_favorites_failed",
  failedTitle: "shell_menu_failed_title",
  favNotSaved: "shell_menu_fav_not_saved",
  /** Итог звёздочки для чтеца: «{label}: додано в обране». */
  favAdded: "shell_menu_fav_added",
  favRemoved: "shell_menu_fav_removed",
  /** Профиль (раздел `S-00`, §3.6 карты): то же слово, что заголовок профиля. */
  profile: "s00_profile_title",
  signOut: "s00_sign_out",
});

/**
 * «Потребує уваги» и числа у пунктов меню (F-013, решение 066). Слово дела — по его
 * виду (`overdue`, `gaps`, …), число — с сервера; причина «не знаю» — слово кода
 * причины, которое отдаёт сервер.
 */
const attention = fromDictionary({
  title: "shell_attention_title",
  /** Заголовок и свёрнутая строка: «Потребує уваги · {n} справ» — база числа названа (правило 5). */
  summary: {
    one: "shell_attention_summary_one",
    few: "shell_attention_summary_few",
    many: "shell_attention_summary_many",
  },
  /** Живая область: «З'явилось {n} нових справ». */
  live: {
    one: "shell_attention_live_one",
    few: "shell_attention_live_few",
    many: "shell_attention_live_many",
  },
  newTag: "shell_attention_new",
  collapse: "shell_attention_collapse",
  expand: "shell_attention_expand",
  /** Число у пункта, когда источник не ответил: не ноль и не пустота (правило 10). */
  unknown: "shell_attention_unknown",
  /** Вторая строка дела «не знаю»: куда пойти проверить. */
  check: "shell_attention_check",
  /** Дело «ліміт»: сколько ещё поставщиков «невідомо» (`unsure`) — «невідомо: {n}» и подсказка (068, 6). */
  unsure: "attn_limit_unsure",
  unsureHint: "attn_limit_unsure_hint",
  /** Дело одной строкой: «3 прострочені оплати», «40 звірок чекають» (число с базой, правило 5). */
  rows: {
    overdue: { one: "attn_row_overdue_one", few: "attn_row_overdue_few", many: "attn_row_overdue_many" },
    rule_overdue: { one: "attn_row_rule_overdue_one", few: "attn_row_rule_overdue_few", many: "attn_row_rule_overdue_many" },
    limit: { one: "attn_row_limit_one", few: "attn_row_limit_few", many: "attn_row_limit_many" },
    gaps: { one: "shell_badge_gaps_one", few: "shell_badge_gaps_few", many: "shell_badge_gaps_many" },
    month_waiting: { one: "attn_row_month_one", few: "attn_row_month_few", many: "attn_row_month_many" },
    wallets: { one: "attn_row_wallets_one", few: "attn_row_wallets_few", many: "attn_row_wallets_many" },
    mismatch: { one: "attn_row_mismatch_one", few: "attn_row_mismatch_few", many: "attn_row_mismatch_many" },
    loan_terms: { one: "attn_row_loan_terms_one", few: "attn_row_loan_terms_few", many: "attn_row_loan_terms_many" },
    payroll_rates: { one: "attn_row_payroll_rates_one", few: "attn_row_payroll_rates_few", many: "attn_row_payroll_rates_many" },
    unsynced: { one: "shell_badge_unsynced_one", few: "shell_badge_unsynced_few", many: "shell_badge_unsynced_many" },
  },
  /** Само обращение за делами не ответило. */
  silent: "shell_attention_silent",
  titles: {
    overdue: "attn_title_overdue",
    rule_overdue: "attn_title_rule_overdue",
    limit: "attn_title_limit",
    gaps: "attn_title_gaps",
    month_waiting: "attn_title_month",
    wallets: "attn_title_wallets",
    mismatch: "attn_title_mismatch",
    loan_terms: "attn_title_loan_terms",
    payroll_rates: "attn_title_payroll_rates",
    unsynced: "attn_title_unsynced",
    sync_down: "attn_title_sync_down",
    backup_failed: "attn_title_backup_failed",
  },
  badges: {
    overdue: "shell_badge_overdue",
    // Полная фраза числа у пункта «Постійні витрати» — для `title` и чтеца; на пункте только цифра.
    rule_overdue: { one: "attn_row_rule_overdue_one", few: "attn_row_rule_overdue_few", many: "attn_row_rule_overdue_many" },
    // Полная фраза числа у пункта «Постачальники» — для `title` и чтеца; на пункте только цифра.
    limit: { one: "attn_row_limit_one", few: "attn_row_limit_few", many: "attn_row_limit_many" },
    gaps: { one: "shell_badge_gaps_one", few: "shell_badge_gaps_few", many: "shell_badge_gaps_many" },
    // Полная фраза числа у пункта — для `title` и чтеца; на самом пункте стоит только цифра.
    month_waiting: {
      one: "shell_badge_month_waiting_one",
      few: "shell_badge_month_waiting_few",
      many: "shell_badge_month_waiting_many",
    },
    unsynced: { one: "shell_badge_unsynced_one", few: "shell_badge_unsynced_few", many: "shell_badge_unsynced_many" },
    wallets: { one: "shell_badge_wallets_one", few: "shell_badge_wallets_few", many: "shell_badge_wallets_many" },
    mismatch: { one: "attn_row_mismatch_one", few: "attn_row_mismatch_few", many: "attn_row_mismatch_many" },
    loan_terms: { one: "attn_row_loan_terms_one", few: "attn_row_loan_terms_few", many: "attn_row_loan_terms_many" },
    payroll_rates: { one: "attn_row_payroll_rates_one", few: "attn_row_payroll_rates_few", many: "attn_row_payroll_rates_many" },
    sync_down: "shell_badge_sync_down",
    backup_failed: "shell_badge_backup_failed",
  },
});

/** Нижняя строка меню: свежесть DnTrade слева (слова индикатора — по коду случая), копия справа. */
const status = fromDictionary({
  label: "shell_status_label",
  /** Подпись левой половины: «DnTrade: свіжо · 22:47». */
  source: "shell_status_dntrade",
  /** «дані застаріли · {n} год». */
  hours: "shell_status_hours",
  /** Короткий заголовок отказа, когда фраза причины идёт обычным текстом под ним. */
  failed: "shell_menu_failed_title",
  /** Подпись правой половины: «Резервна копія: 02.10, 12:00». */
  backup: "shell_status_backup",
  backupOk: "shell_status_backup_ok",
  backupOkSr: "shell_status_backup_ok_sr",
  backupFailed: "shell_status_backup_failed",
  backupNone: "shell_status_backup_none",
  backupUnknown: "shell_status_backup_unknown",
});

const header = fromDictionary({
  // ⚠⚠ Фраз индикатора (свіжо, давно не оновлювалось, …) здесь нет: их код
  // отдаёт сервер полем `indicator` (решение 83), слово — словарь по коду.
  freshness: {
    /** Имя области шапки для чтеца: одним словом «индикатор» ничего не значит. */
    label: "header_freshness",
    /** Подпись перехода на поверхность — название поверхности, а не код. */
    goTo: "s10_title",
    // `НВ`: переход не рисуется мёртвым пунктом (`DEC-O527`, п. 1); имени
    // пресета в строке нет (`G81`, `DEC-O526`).
    hiddenWhat: "header_hidden_what",
    hiddenHow: "header_hidden_how",
    // `ПМ`: не ответила наша же страница, а не DnTrade.
    silentCause: "header_silent_cause",
    silentWayOut: "header_silent_way_out",
    hint: hint("header_freshness", "DEC-O21"),
    /** Шкала свежести в «?»: пояснение ступени; слово ступени — код случая. */
    scale: {
      fresh: "fresh_scale_fresh",
      stale: "fresh_scale_stale",
      down: "fresh_scale_down",
      /** «Межа … — {minutes} хв» — только где интервал пришёл в ответе. */
      limit: "fresh_scale_limit",
    },
    /** «дані DnTrade на {when}» — источник и время в «?». */
    source: "source_dntrade",
  },
});

/** Пояснения направлений долга в легенде «?»: слово направления — `DIRECTION_WORDS`. */
const direction = fromDictionary({
  weOwe: "direction_we_owe_text",
  theyOwe: "direction_they_owe_text",
  none: "direction_none_text",
});

/** Значения, общие всем экранам: неизвестное — словом, а не нулём. */
const values = fromDictionary({ unknown: "value_unknown" });

/** Отбор длинной таблицы (`components/ui/table-view.tsx`): одни слова на всех экранах. */
const table = fromDictionary({
  search: "table_search",
  filters: "table_filters",
  /** «Знайдено {n} з {total}» — строк на экране, не величина. */
  found: "table_found",
  nothing: "table_nothing",
  reset: "table_reset",
  /** «Згорнути» — у любого раскрытия «Показати все / ще N». */
  collapse: "fold_collapse",
});

export const uk = { appName, shell, nav, attention, status, header, direction, values, table } as const;

export type Uk = typeof uk;
